import type { HTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';
import VC from 'vanilla-calendar-pro';
import type { IOptions } from 'vanilla-calendar-pro/types';
import 'vanilla-calendar-pro/build/vanilla-calendar.min.css';
import { SCalendar } from './VanillaCalendar.styles';
import i18n, { ELanguages } from '../../i18n';

const LanguageCodesMap: Record<string, string> = {
  se: 'sv',
  fi: 'fi',
  en: 'en',
  'en-TT': 'en',
  nl: 'nl',
  es: 'es',
  pt: 'pt-BR',
};

type VanillaCalendarProps = {
  config?: IOptions;
  initialDate?: string | null;
  handleSelected: (isoDate: string) => void;
} & HTMLAttributes<HTMLDivElement>;

const calendarSettings: IOptions = {
  type: 'default',
  date: {
    min: 'today',
  },
  settings: {
    visibility: {
      theme: 'dark',
      weekend: false,
      daysOutside: false,
    },
    range: {
      min: 'today',
    },
  },
  CSSClasses: {
    calendar: 'calendar',
    weekDay: 'weekDay',
    dayBtn: 'dayBtn',
    dayBtnDisabled: 'dayBtnDisabled',
    dayBtnSelected: 'dayBtnSelected',
    dayBtnToday: 'dayBtnToday',
  },
};

function VanillaCalendar({
  config,
  handleSelected,
  initialDate,
  children,
  ...attributes
}: VanillaCalendarProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [calendar, setCalendar] = useState<VC | null>(null);
  const initiallySelected = initialDate
    ? {
        dates: [initialDate],
        month: new Date(initialDate).getMonth(),
        year: new Date(initialDate).getFullYear(),
      }
    : {};
  const calendarLanguage = LanguageCodesMap[i18n.language] ?? LanguageCodesMap[ELanguages.en];

  useEffect(() => {
    if (!ref.current) return;
    setCalendar(
      new VC(ref.current, {
        ...calendarSettings,
        actions: {
          clickDay(event, self) {
            if (!self.selectedDates?.length) return;
            handleSelected(self.selectedDates[0]);
          },
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        settings: {
          ...calendarSettings.settings,
          ...{ selected: initiallySelected },
          lang: calendarLanguage,
        },
        ...config,
      })
    );
  }, [ref, config]);

  useEffect(() => {
    if (!calendar) return;
    calendar.init();
  }, [calendar]);

  return <SCalendar {...attributes} ref={ref} />;
}

export default VanillaCalendar;
